import { useState } from 'react';
import { useMutation } from './query.js';
import { api, ApiError, type PromoteResult } from './api.js';

type Scope = 'concept' | 'cluster' | 'all';
type Action = 'promote' | 'retire';
type Target = 'study' | 'battle' | 'both';

/**
 * Guarded promote/retire modal. ALWAYS runs a dry-run plan first, shows the plan +
 * counts + any rows blocked by the approval guard, then requires an explicit
 * "Confirm" click before applying.
 */
export function PromoteModal({
  clusterId,
  conceptCode,
  onClose,
  onToast,
}: {
  clusterId: string | null;
  conceptCode: string | null;
  onClose: () => void;
  onToast: (msg: string, kind: 'success' | 'error') => void;
}) {
  const [action, setAction] = useState<Action>('promote');
  const [target, setTarget] = useState<Target>('both');
  const [scope, setScope] = useState<Scope>(conceptCode ? 'concept' : clusterId ? 'cluster' : 'all');
  const [plan, setPlan] = useState<PromoteResult | null>(null);

  function buildBody() {
    const body: Record<string, unknown> = { action, target };
    if (scope === 'concept' && conceptCode) body.concept = conceptCode;
    else if (scope === 'cluster' && clusterId) body.cluster = clusterId;
    else body.all = true;
    return body;
  }

  const planMut = useMutation({
    mutationFn: () => api.post<PromoteResult>('/api/promote/plan', buildBody()),
    onSuccess: (res) => setPlan(res),
    onError: (e) => onToast(`Plan failed: ${(e as ApiError).message}`, 'error'),
  });

  const applyMut = useMutation({
    mutationFn: () => api.post<PromoteResult>('/api/promote/apply', buildBody()),
    onSuccess: (res) => {
      const changed = res.studyChanged + res.battleChanged;
      onToast(`${action === 'promote' ? 'Promoted' : 'Retired'} ${changed} row(s).`, 'success');
      onClose();
    },
    onError: (e) => {
      const err = e as ApiError;
      if (err.status === 409) {
        onToast('Blocked: scope contains un-approved DRAFT rows. Approve them first.', 'error');
        // surface the skipped rows in the plan view
        const body = err.body as { skippedUnapproved?: PromoteResult['skippedUnapproved'] };
        if (body?.skippedUnapproved) {
          setPlan((p) =>
            p ? { ...p, skippedUnapproved: body.skippedUnapproved! } : p,
          );
        }
      } else {
        onToast(`Apply failed: ${err.message}`, 'error');
      }
    },
  });

  // Reset plan whenever the selection changes (force re-running dry-run).
  function changeAndReset<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPlan(null);
    };
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="modal-promote">
        <h2>Promote / Retire content</h2>

        <label>Action</label>
        <select value={action} onChange={(e) => changeAndReset(setAction)(e.target.value as Action)} data-testid="select-action">
          <option value="promote">Promote (DRAFT → ACTIVE)</option>
          <option value="retire">Retire (ACTIVE → RETIRED)</option>
        </select>

        <label>Target</label>
        <select value={target} onChange={(e) => changeAndReset(setTarget)(e.target.value as Target)} data-testid="select-target">
          <option value="both">Study + Battle</option>
          <option value="study">Study only</option>
          <option value="battle">Battle only</option>
        </select>

        <label>Scope</label>
        <select value={scope} onChange={(e) => changeAndReset(setScope)(e.target.value as Scope)} data-testid="select-scope">
          <option value="concept" disabled={!conceptCode}>
            This concept{conceptCode ? ` (${conceptCode})` : ' (none selected)'}
          </option>
          <option value="cluster" disabled={!clusterId}>
            This cluster{clusterId ? ` (${clusterId})` : ' (none selected)'}
          </option>
          <option value="all">All</option>
        </select>

        <div className="row-actions" style={{ marginTop: 14 }}>
          <button
            className="primary"
            onClick={() => planMut.mutate()}
            disabled={planMut.isPending}
            data-testid="button-run-plan"
          >
            {planMut.isPending ? 'Planning…' : 'Preview plan (dry-run)'}
          </button>
          <button className="ghost" onClick={onClose} data-testid="button-cancel-promote">
            Cancel
          </button>
        </div>

        {plan && (
          <div style={{ marginTop: 16 }} data-testid="plan-result">
            <hr className="sep" />
            <p>
              <strong>{plan.plan.length}</strong> row(s) will change · study {plan.studyChanged} · battle{' '}
              {plan.battleChanged} · no-op (study {plan.studyNoop} / battle {plan.battleNoop}) · skipped (study{' '}
              {plan.studySkipped} / battle {plan.battleSkipped})
            </p>
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {plan.plan.map((r) => (
                <div className="plan-row" key={r.id}>
                  <span className="lbl">{r.label}</span>
                  <span className="muted">
                    {r.fromStatus} → {r.toStatus}
                  </span>
                </div>
              ))}
            </div>

            {action === 'promote' && plan.skippedUnapproved.length > 0 && (
              <div style={{ marginTop: 10 }} data-testid="plan-blocked">
                <p className="err">
                  {plan.skippedUnapproved.length} row(s) in scope are NOT approved and block promotion:
                </p>
                <div style={{ maxHeight: 140, overflowY: 'auto' }}>
                  {plan.skippedUnapproved.map((r) => (
                    <div className="plan-row" key={r.id}>
                      <span className="lbl">{r.label}</span>
                      <span className="err">not approved</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="row-actions" style={{ marginTop: 14 }}>
              <button
                className={action === 'retire' ? 'danger' : 'primary'}
                onClick={() => applyMut.mutate()}
                disabled={
                  applyMut.isPending ||
                  plan.plan.length === 0 ||
                  (action === 'promote' && plan.skippedUnapproved.length > 0)
                }
                data-testid="button-confirm-apply"
              >
                {applyMut.isPending
                  ? 'Applying…'
                  : `Confirm ${action === 'promote' ? 'promote' : 'retire'}`}
              </button>
              {action === 'promote' && plan.skippedUnapproved.length > 0 && (
                <span className="muted">Resolve un-approved rows or narrow the scope to enable.</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
